import { useMemo } from 'react';
import { useList, useMany } from '@refinedev/core';
import type {
  AppAdminTenancySchemasContractContractOut as ContractOut,
  OrganizationOut,
  OrganizationPublisherOut,
} from '@/api/generated/schemas';
import {
  resolvePublisherStanding,
  type PublisherStanding,
} from '@/components/tenancy/publisherStanding';

// A publisher holds a handful of links; an organization a few dozen contracts.
const LINKS_LIMIT = 100;
const CONTRACTS_LIMIT = 1000;

export interface PublisherStandingResult {
  standing: PublisherStanding;
  organizationName: (id: number) => string | undefined;
  isLoading: boolean;
  isError: boolean;
}

/**
 * The publisher's organizations and contract coverage, resolved against the
 * backend's enable rule (`resolvePublisherStanding`).
 *
 * Two dependent requests: every link of the publisher, then the contracts of
 * the organizations those links point to (filtered to this publisher and the
 * organization-wide ones client-side — the API has no `publisher_id` null
 * filter). Refine invalidates both lists on any tenancy change.
 */
export const usePublisherStanding = (
  publisherId: number | undefined,
  { enabled = true }: { enabled?: boolean } = {}
): PublisherStandingResult => {
  const { result: linksResult, query: linksQuery } =
    useList<OrganizationPublisherOut>({
      resource: 'tenancy.organization_publishers',
      filters: [{ field: 'publisher_id', operator: 'eq', value: publisherId }],
      sorters: [{ field: 'valid_from', order: 'asc' }],
      pagination: { pageSize: LINKS_LIMIT },
      queryOptions: { enabled: enabled && publisherId != null },
    });
  const links = linksResult?.data;

  const organizationIds = useMemo(
    () => [...new Set((links ?? []).map((link) => link.organization_id))],
    [links]
  );
  const withOrganizations = enabled && organizationIds.length > 0;

  const { result: contractsResult, query: contractsQuery } =
    useList<ContractOut>({
      resource: 'tenancy.contracts',
      filters: [
        { field: 'organization_id', operator: 'in', value: organizationIds },
      ],
      pagination: { pageSize: CONTRACTS_LIMIT },
      queryOptions: { enabled: withOrganizations },
    });
  const contracts = contractsResult?.data;

  const { result: organizationsResult } = useMany<OrganizationOut>({
    resource: 'tenancy.organizations',
    ids: organizationIds,
    queryOptions: { enabled: withOrganizations },
  });
  const organizations = organizationsResult?.data;

  const standing = useMemo(
    () =>
      resolvePublisherStanding(publisherId ?? -1, links ?? [], contracts ?? []),
    [publisherId, links, contracts]
  );
  const names = useMemo(
    () => new Map((organizations ?? []).map((o) => [o.id, o.name])),
    [organizations]
  );

  return {
    standing,
    organizationName: (id) => names.get(id),
    isLoading:
      linksQuery.isLoading || (withOrganizations && contractsQuery.isLoading),
    isError: linksQuery.isError || contractsQuery.isError,
  };
};
